// src/pages/MPFlow/phases/Phase4Wallet.jsx
// ─── WALLET TURBO — Créditos + Verba de Anúncio ────────────────────────────
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CreditCardIcon,
  RocketLaunchIcon,
  SparklesIcon,
  BoltIcon,
  ShieldCheckIcon,
  ClipboardDocumentIcon,
} from '@heroicons/react/24/outline';
import EdyMascot from '../components/EdyMascot';
import apiClient from '../../../lib/apiClient';

// ── Confetti Canvas (zero dep) ─────────────────────────────────────────────
function ConfettiCanvas({ active }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!active || !ref.current) return;
    const canvas = ref.current;
    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    const colors = ['#06B6D4', '#7E22CE', '#FBBF24', '#4ADE80', '#F472B6', '#fff'];
    const pieces = Array.from({ length: 120 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * -canvas.height,
      w: 6 + Math.random() * 6,
      h: 4 + Math.random() * 4,
      color: colors[Math.floor(Math.random() * colors.length)],
      vx: (Math.random() - 0.5) * 3,
      vy: 2 + Math.random() * 4,
      rot: Math.random() * Math.PI * 2,
      vr: (Math.random() - 0.5) * 0.15,
    }));
    let frame;
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      let alive = false;
      pieces.forEach(p => {
        p.x += p.vx; p.y += p.vy; p.rot += p.vr;
        if (p.y < canvas.height + 20) alive = true;
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        ctx.restore();
      });
      if (alive) frame = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(frame);
  }, [active]);
  if (!active) return null;
  return <canvas ref={ref} className="mp-confetti-overlay" />;
}

// ── Componente principal ───────────────────────────────────────────────────
export default function Phase4Wallet({ profile, creation, refinement, campaignId: propCampId, isLoggedIn, onNext, onBack }) {
  const navigate = useNavigate();

  // Estados
  const [phase, setPhase]               = useState('packages'); // packages | checkout | confirming | done
  const [packages, setPackages]         = useState([]);
  const [selectedPkg, setSelectedPkg]   = useState(null);
  const [wallet, setWallet]             = useState(null);
  const [campaignId, setCampaignId]     = useState(propCampId || null);
  const [paymentId, setPaymentId]       = useState('');
  const [pixCode, setPixCode]           = useState('');
  const [copied, setCopied]             = useState(false);
  const [payMethod, setPayMethod]       = useState('pix');
  const [error, setError]               = useState('');
  const [loading, setLoading]           = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [authRequired, setAuthRequired] = useState(false);
  const [cpm, setCpm]                   = useState(25);

  // Carrega pacotes + wallet ao montar
  useEffect(() => {
    (async () => {
      try {
        const nicho = profile?.nicho || '';
        const res = await apiClient.request(`/turbo/packages?nicho=${encodeURIComponent(nicho)}`, { method: 'GET' });
        if (res?.packages) {
          setPackages(res.packages);
          setCpm(res.cpm || 25);
        }
      } catch {}
    })();
  }, [profile?.nicho]);

  const loadWallet = useCallback(async () => {
    if (!isLoggedIn) return;
    try {
      const res = await apiClient.request('/turbo/wallet', { method: 'GET' });
      if (res?.wallet) setWallet(res.wallet);
    } catch {}
  }, [isLoggedIn]);

  useEffect(() => { loadWallet(); }, [loadWallet]);

  // Salva campanha se necessário
  const ensureCampaign = async () => {
    if (campaignId) return campaignId;
    try {
      const res = await apiClient.request('/mp/campaign', {
        method: 'POST',
        body: JSON.stringify({
          session_id:       profile.session_id || '',
          nicho:            profile.nicho,
          diferencial:      profile.diferencial,
          dor_cliente:      profile.dor_cliente,
          headline:         creation.copy?.headline   || '',
          legenda:          creation.copy?.legenda    || '',
          image_url:        refinement?.imageUrl      || '',
          destino:          refinement?.destino       || '',
          tom:              refinement?.tom            || 'profissional',
          publico_sugerido: creation.copy?.publico_alvo || creation.copy?.publico_sugerido || {},
        }),
      });
      const id = res?.campaign_id;
      if (id) { setCampaignId(id); return id; }
    } catch {}
    return null;
  };

  // ── Iniciar compra ─────────────────────────────────────
  const startBuy = async () => {
    if (!isLoggedIn) { setAuthRequired(true); return; }
    if (!selectedPkg) { setError('Selecione um pacote.'); return; }

    setLoading(true);
    setError('');
    try {
      const cid = await ensureCampaign();
      if (!cid) throw new Error('Falha ao criar campanha');

      const res = await apiClient.request('/turbo/buy', {
        method: 'POST',
        body: JSON.stringify({
          package_id:  selectedPkg,
          method:      payMethod,
          campaign_id: cid,
        }),
      });

      setPaymentId(res?.payment_id || '');
      setPixCode(res?.pix_code || '');
      setPhase('checkout');
    } catch {
      setError('Erro ao iniciar pagamento. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  // ── Confirmar pagamento (via webhook) ──────────────────
  const confirmPayment = async () => {
    if (!paymentId) return;
    setPhase('confirming');
    try {
      const res = await apiClient.request('/turbo/webhook', {
        method: 'POST',
        body: JSON.stringify({
          payment_id: paymentId,
          event:      'payment.confirmed',
        }),
      });

      if (res?.success) {
        setShowConfetti(true);
        await loadWallet();
        setPhase('done');
        // Auto-avança para dashboard após 2.5s
        setTimeout(() => {
          onNext({ campaignId });
        }, 2500);
      } else {
        setError('Confirmacao falhou. Tente novamente.');
        setPhase('checkout');
      }
    } catch {
      setError('Erro ao confirmar pagamento.');
      setPhase('checkout');
    }
  };

  const copyPix = () => {
    navigator.clipboard.writeText(pixCode).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // Encontra pacote selecionado
  const activePkg = packages.find(p => p.id === selectedPkg);

  // ── Auth Gate ──────────────────────────────────────────
  if (authRequired && !isLoggedIn) {
    return (
      <div className="mp-card mp-auth-gate">
        <ShieldCheckIcon className="icon-2xl icon-cyan" />
        <h3 className="mp-auth-gate__title">Crie sua conta para continuar</h3>
        <p className="mp-auth-gate__sub">
          O pagamento exige uma conta para proteger seus dados e ativar sua campanha.
        </p>
        <button className="mp-btn mp-btn--primary" onClick={() => navigate('/signup')}>
          Criar conta gratis
        </button>
        <button className="mp-btn mp-btn--ghost mp-btn--sm" onClick={() => navigate('/login')}>
          Ja tenho conta
        </button>
      </div>
    );
  }

  // ── Confirmando ────────────────────────────────────────
  if (phase === 'confirming') {
    return (
      <div className="mp-loading">
        <div className="mp-loading__icon"><CreditCardIcon className="icon-xl" /></div>
        <div className="mp-loading__dots"><span /><span /><span /></div>
        <p className="mp-loading__msg">Confirmando seu pagamento...</p>
      </div>
    );
  }

  // ── Sucesso com confetti + foguete ─────────────────────
  if (phase === 'done') {
    return (
      <>
        <ConfettiCanvas active={showConfetti} />
        <div className="mp-card mp-auth-gate mp-turbo-done">
          <div className="mp-turbo-rocket">
            <RocketLaunchIcon className="icon-3xl icon-cyan" />
          </div>
          <h3 className="mp-auth-gate__title">Campanha Ativada!</h3>
          <p className="mp-auth-gate__sub">Seus anuncios estao no ar. Redirecionando para o painel...</p>
          {wallet && (
            <div className="mp-wallet-cards mp-wallet-cards--compact">
              <div className="mp-wallet-card">
                <div className="mp-wallet-card__value">{wallet.unlimited_credits ? 'Ilimitado' : wallet.internal_credits}</div>
                <div className="mp-wallet-card__label">Creditos IA</div>
              </div>
              <div className="mp-wallet-card">
                <div className="mp-wallet-card__value">R$ {(wallet.ad_balance || 0).toFixed(2)}</div>
                <div className="mp-wallet-card__label">Verba de Anuncio</div>
              </div>
            </div>
          )}
        </div>
      </>
    );
  }

  // ── MODAL PRINCIPAL ────────────────────────────────────
  return (
    <div>
      <EdyMascot phase="turbinar" />
      <ConfettiCanvas active={showConfetti} />

      {onBack && phase === 'packages' && (
        <button className="mp-btn mp-btn--ghost mp-btn--sm mp-mb-4" onClick={onBack}>
          ← Voltar ao Refino
        </button>
      )}

      {error && <div className="mp-alert mp-alert--error">{error}</div>}

      {/* ── Saldo atual ─────────────────────────── */}
      {isLoggedIn && wallet && (
        <div className="mp-card mp-turbo-glass mp-mb-4">
          <h3 className="mp-turbo-section-title">
            <SparklesIcon className="icon-sm" /> Sua Carteira Turbo
          </h3>
          <div className="mp-wallet-cards">
            <div className="mp-wallet-card">
              <div className="mp-wallet-card__value">{wallet.unlimited_credits ? 'Ilimitado' : wallet.internal_credits}</div>
              <div className="mp-wallet-card__label">Creditos IA + Especialista</div>
            </div>
            <div className="mp-wallet-card">
              <div className="mp-wallet-card__value">R$ {(wallet.ad_balance || 0).toFixed(2)}</div>
              <div className="mp-wallet-card__label">Verba de Anuncio (midia paga)</div>
            </div>
          </div>
        </div>
      )}

      {/* ── Seleção de pacotes ───────────────────── */}
      {phase === 'packages' && (
        <div className="mp-card mp-turbo-glass">
          <h2 className="mp-phase-title">
            <BoltIcon className="icon-md icon-inline" />
            Recarga Turbo
          </h2>
          <p className="mp-phase-sub">Escolha seu pacote para ativar e impulsionar sua campanha.</p>

          {/* Barra de progresso "Faltam 2 cliques" */}
          <div className="mp-turbo-steps-bar">
            <div className="mp-turbo-steps-bar__fill" style={{ width: phase === 'packages' ? '33%' : '66%' }} />
            <span className="mp-turbo-steps-bar__label">Faltam 2 cliques para ativar</span>
          </div>

          <div className="mp-plans-grid">
            {packages.map(pkg => (
              <div
                key={pkg.id}
                className={`mp-plan-card ${pkg.id === 'pro' ? 'mp-plan-card--featured mp-turbo-glow' : ''} ${selectedPkg === pkg.id ? 'mp-plan-card--selected' : ''}`}
                onClick={() => setSelectedPkg(pkg.id)}
              >
                <div className="mp-plan-card__info">
                  <div className="mp-plan-card__name">
                    {pkg.name}
                    {pkg.id === 'pro' && <span className="mp-plan-card__badge mp-ml-2">Popular</span>}
                  </div>
                  <div className="mp-plan-card__features">
                    {pkg.credits === -1 ? 'Creditos ilimitados' : `${pkg.credits} creditos IA`}
                    {' + '}R${pkg.ad_value} em anuncios
                  </div>
                  {/* Calculadora de alcance */}
                  <div className="mp-turbo-reach">
                    Alcance estimado: ~{(pkg.alcance_estimado || 0).toLocaleString('pt-BR')} pessoas
                  </div>
                </div>
                <div className="mp-plan-card__price">R${pkg.price}</div>
              </div>
            ))}
          </div>

          {/* Nota legal da calculadora */}
          <p className="mp-turbo-cpm-note">
            Estimativa baseada em media de mercado para o nicho {profile?.nicho || 'informado'} (CPM R${cpm}).
          </p>

          {/* Método de pagamento */}
          <div className="mp-turbo-pay-methods">
            <button
              className={`mp-turbo-pay-btn ${payMethod === 'pix' ? 'mp-turbo-pay-btn--active' : ''}`}
              onClick={() => setPayMethod('pix')}
            >
              PIX
            </button>
            <button
              className={`mp-turbo-pay-btn ${payMethod === 'card' ? 'mp-turbo-pay-btn--active' : ''}`}
              onClick={() => setPayMethod('card')}
            >
              Cartao
            </button>
          </div>

          <button
            className="mp-btn mp-btn--primary mp-turbo-cta"
            onClick={startBuy}
            disabled={!selectedPkg || loading}
          >
            {loading ? 'Aguarde...' : 'Ativar Campanha'}
          </button>
        </div>
      )}

      {/* ── Checkout PIX ───────────────────────── */}
      {phase === 'checkout' && payMethod === 'pix' && (
        <div className="mp-card mp-turbo-glass">
          {/* Barra de progresso atualizada */}
          <div className="mp-turbo-steps-bar">
            <div className="mp-turbo-steps-bar__fill" style={{ width: '66%' }} />
            <span className="mp-turbo-steps-bar__label">Falta 1 clique para ativar</span>
          </div>

          <h2 className="mp-phase-title">Pagamento PIX</h2>
          <p className="mp-phase-sub">Copie o codigo e pague. Apos o pagamento, clique em confirmar.</p>

          {activePkg && (
            <div className="mp-turbo-summary">
              <span>{activePkg.name}</span>
              <span className="mp-turbo-summary__price">R${activePkg.price}</span>
            </div>
          )}

          <div className="mp-pix-box">
            <p className="mp-pix-label">Codigo PIX Copia e Cola:</p>
            <div className="mp-pix-code">
              <code>{pixCode}</code>
              <button className="mp-btn mp-btn--ghost mp-btn--sm" onClick={copyPix}>
                <ClipboardDocumentIcon className="icon-xs icon-inline" />
                {copied ? 'Copiado!' : 'Copiar'}
              </button>
            </div>
          </div>

          <div className="mp-alert mp-alert--info mp-mb-5">
            Apos realizar o pagamento no seu banco, clique no botao abaixo para confirmar.
          </div>

          <button className="mp-btn mp-btn--primary mp-turbo-cta" onClick={confirmPayment}>
            Confirmar Pagamento
          </button>
        </div>
      )}

      {/* ── Checkout Cartão ────────────────────── */}
      {phase === 'checkout' && payMethod === 'card' && (
        <div className="mp-card mp-turbo-glass">
          <div className="mp-turbo-steps-bar">
            <div className="mp-turbo-steps-bar__fill" style={{ width: '66%' }} />
            <span className="mp-turbo-steps-bar__label">Falta 1 clique para ativar</span>
          </div>

          <h2 className="mp-phase-title">Pagamento Cartao</h2>

          {activePkg && (
            <div className="mp-turbo-summary">
              <span>{activePkg.name}</span>
              <span className="mp-turbo-summary__price">R${activePkg.price}</span>
            </div>
          )}

          <div className="mp-alert mp-alert--info mp-my-4">
            Clique abaixo para confirmar a cobranca no cartao salvo.
          </div>

          <button className="mp-btn mp-btn--primary mp-turbo-cta" onClick={confirmPayment}>
            Confirmar Pagamento
          </button>
        </div>
      )}
    </div>
  );
}
